import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { SearchableSelect } from '../../../../components/ui';
import { mergeSlotProps } from '../../../../components/ui/SearchableSelect/selectUtils';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import useInterveneOrgAgents from '../../hooks/useInterveneOrgAgents';

const filterUsers = createFilterOptions({
  stringify: (option) => `${option.label || ''} ${option.profile_name || ''} ${option.username || ''}`,
});

const dropdownSlotProps = mergeSlotProps(
  {
    popper: {
      sx: { zIndex: 1400 },
    },
    paper: {
      sx: {
        borderRadius: 2,
        boxShadow: 4,
        mt: 0.5,
        maxHeight: 320,
      },
    },
    listbox: {
      sx: {
        py: 0.5,
        '& .MuiAutocomplete-option': {
          alignItems: 'flex-start',
          py: 1.25,
          minHeight: 48,
        },
      },
    },
  },
  {}
);

function agentOptionLabel(agent) {
  if (!agent) return '';
  return agent.display_name || agent.username || '';
}

export default function InterveneAgentSelect({
  orgId,
  value = '',
  onChange,
  label,
  placeholder,
  helperText,
  disabled = false,
}) {
  const { agents, isLoading } = useInterveneOrgAgents(orgId);

  const options = useMemo(
    () => agents.map((agent) => ({
      id: agent.username,
      label: agentOptionLabel(agent),
      username: agent.username,
      user_id: agent.user_id,
      display_name: agent.display_name,
      profile_name: agent.profile_name,
    })),
    [agents]
  );

  const selected = options.find((item) => item.id === value) || null;
  const resolvedHelper = !isLoading && !options.length
    ? ANALYTICS_COPY.interveneUserListEmpty
    : [
      options.length
        ? ANALYTICS_COPY.interveneUserCountHint.replace('{count}', String(options.length))
        : '',
      helperText,
    ].filter(Boolean).join(' · ');

  const renderOption = (props, option) => {
    const { key, ...optionProps } = props;
    return (
      <Box
        component="li"
        key={key}
        {...optionProps}
        sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', width: '100%' }}
      >
        <PersonOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.25 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap title={option.label}>
            {option.label}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            display="block"
            title={option.profile_name || option.username}
          >
            {option.profile_name || option.username}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <SearchableSelect
      label={label}
      placeholder={placeholder}
      options={options}
      value={selected}
      onChange={(opt) => onChange({
        username: opt?.id || '',
        userId: opt?.user_id != null ? String(opt.user_id) : '',
      })}
      getOptionLabel={(opt) => opt?.label || ''}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      renderOption={renderOption}
      filterOptions={filterUsers}
      disabled={disabled}
      loading={isLoading}
      helperText={resolvedHelper}
      noOptionsText={ANALYTICS_COPY.interveneUserListEmpty}
      slotProps={dropdownSlotProps}
      clearable
    />
  );
}
